// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, FormControlLabel, InputLabel, Stack, Switch, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseTable,
  BaseTableOption,
  BaseTableUser,
  BaseToolbar,
  ConfirmModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { StorageEnum } from '~/utils/enum/base';
import { IPaginationResponse } from '~/utils/interface/base';
import useDebounce from '~/utils/hooks/useDebounce';
import { IQueryParamTask, ITask } from '~/utils/interface/task';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { formatDate, formatTitleCase, getFullName } from '~/utils/helper/common';
import { DateFormatEnum, RoleEnum, SearchByFilterContentEnum, StatusEnum } from '~/utils/enum/common';
import { IBaseOption, IFilterValueChange, IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { getEmployeeList } from '~/thunks/employee/employeeThunk';
import { deleteTask, getTaskList } from '~/thunks/task/taskThunk';
import { selectIsRefreshTaskList, taskActions } from '~/thunks/task/taskSlice';
// Styles, images, icons
import styles from './TaskToday.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsTask = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void
): IHeaderCellType<ITask>[] => {
  return [
    {
      id: 'title',
      label: t('table_head_title_name'),
      render: (record: ITask) => record?.title || EMPTY_STRING,
    },
    {
      id: 'assignee',
      label: t('table_head_title_employee'),
      render: (record: ITask) => {
        const { assignee } = record;
        if (!assignee) return;
        const { avatarImage, firstName, lastName } = assignee;
        return <BaseTableUser user={{ avatar: avatarImage, firstName, lastName }} />;
      },
    },
    {
      id: 'date',
      label: t('table_head_title_due_date'),
      render: (record: ITask) => {
        return record?.dueDate ? formatDate(record?.dueDate, DateFormatEnum.DD_MMM_YYYY) : EMPTY_STRING;
      },
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      align: 'left',
      render: (record: ITask) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            onDelete={() => onDelete(record.id)}
          />
        );
      },
    },
  ];
};

const TaskToday = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/task', 'admin/dashboard']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );
  //#endregion Declare Hook

  //#region Selector
  const isRefreshTaskList = useSelector(selectIsRefreshTaskList);
  //#endregion Selector

  //#region Declare State
  const [accountId] = useState<string | null>(localStorage.getItem(StorageEnum.ACCOUNT_ID));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [currentRole] = useState<string | null>(localStorage.getItem(StorageEnum.ROLE));

  const [filterParams, setFilterParams] = useState<IQueryParamTask>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IQueryParamTask>({
    page: pageSelected,
    limit: limitPage,
    dueDate: formatDate(new Date(), DateFormatEnum.YYYY_MM_DD),
  });
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [taskList, setTaskList] = useState<ITask[]>([]);
  const [dataId, setDataId] = useState<string>(EMPTY_STRING);
  const [employeeList, setEmployeeList] = useState<IBaseOption[]>([]);
  const [isGetAllTask, setIsGetAllTask] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetEmployeeList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newParams = isGetAllTask
      ? paramObject
      : {
          ...paramObject,
          employeeIds: [accountId],
        };

    handleGetTaskList(newParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject, isGetAllTask]);

  useEffect(() => {
    if (!isRefreshTaskList) return;

    handleGetTaskList(paramObject);
    dispatch(taskActions.setRefreshTaskList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTaskList]);

  useEffect(() => {
    setSearchKey(textSearchParams);
  }, [textSearchParams]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields?.map((item) => formatTitleCase(item))?.join(', ');
      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      // setSearchParamsTask({
      //   page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      //   limit: String(limitPageTask),
      //   ...(searchKeyTask ? { textSearch: debounceSearchKeyTask } : {}),
      // });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (!searchKey) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      ...(filterParams ? { ...filterParams } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetEmployeeList = () => {
    const params: IQueryParamBase = {
      page: DEFAULT_NUMBER_ONE,
      limit: DEFAULT_LIMIT_1000,
      status: StatusEnum.ACTIVE,
    };
    dispatch(getEmployeeList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;
        if (!responses) return;

        const dataEmployee: IBaseOption[] = responses.map((item) => ({
          value: item.id,
          label: getFullName({ firstName: item.firstName, lastName: item.lastName }),
        }));

        setEmployeeList(dataEmployee);
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleGetTaskList = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getTaskList(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res.data;

        if (!responses) return;

        setTaskList(responses);
        setPagination(pagination);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleApplyFilter = (values: IQueryParamBase | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    setParamObject({
      ...paramObject,
      ...values,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleFilterEmployeeChange = (
    options: string[],
    onChange?: (value: IFilterValueChange<IQueryParamTask>) => void
  ) => {
    if (!options) return;

    onChange?.({ name: 'employeeIds', value: options });
  };

  const handleViewTask = (taskId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.tasks}/${taskId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.tasks}/${taskId}`);
        break;
    }
  };

  const handleDeleteTask = (id: string) => {
    if (!id) return;

    setDataId(id);
    setIsOpenConfirm(true);
  };

  const handleCloseConfirmDelete = () => {
    setIsOpenConfirm(false);
  };

  const handleConfirmDelete = () => {
    if (!dataId) return;
    setIsLoading(true);

    dispatch(deleteTask(dataId))
      .unwrap()
      .then(() => {
        setDataId(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(taskActions.setRefreshTaskList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditTask = (taskId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.updateTask(taskId)}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.updateTask(taskId)}`);
        break;
    }
  };

  const handleRowTableClick = (taskId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.tasks}/${taskId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.tasks}/${taskId}`);
        break;
    }
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    // setSearchParamsTask({
    //   ...paramsTask,
    //   page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    //   limit: String(rowPerPage),
    // });
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    // setSearchParamsTask({ ...paramsTask, page: String(page), limit: String(limitPageTask) });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleSwitchAllTaskChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    setIsGetAllTask(checked);
  };
  //#endregion Handle Function

  return (
    <Box id='taskTodayComponent' className={cx('containerTask')}>
      <BaseToolbar title={t('dashboard_task_for_today', { ns: 'admin/dashboard' })}>
        <FormControlLabel
          control={<Switch checked={isGetAllTask} onChange={handleSwitchAllTaskChange} />}
          label={t('task_switch_all_task_label')}
          labelPlacement='start'
          className={cx('switch')}
          slotProps={{ typography: { className: cx('switchLabel') } }}
        />

        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.NAME}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.NAME
          }
          value={searchKey}
          onChange={handleSearchChange}
          width={160}
        />
        {isGetAllTask && (
          <BaseFilters<IQueryParamTask>
            value={filterParams}
            defaultValue={FILTER_DEFAULT_VALUES}
            onApply={handleApplyFilter}
          >
            {({ value, onChange }) => (
              <Stack gap={'12px'}>
                <Typography className={cx('title')}>
                  {t('common_action_filter', { ns: 'common/base' })}
                </Typography>

                <Box component={'div'} className={cx('filterGroup')}>
                  <InputLabel className={cx('label')}>{t('filter_label_employee')}</InputLabel>

                  <BaseSelect
                    multiple
                    layout='horizontal'
                    width={'175px'}
                    options={employeeList || []}
                    value={value?.employeeIds}
                    onChange={(optionSelected: any) => {
                      handleFilterEmployeeChange(optionSelected, onChange);
                    }}
                  />
                </Box>
              </Stack>
            )}
          </BaseFilters>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('bodyTask')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ONE}
          columns={columnsTask(t, handleViewTask, handleEditTask, handleDeleteTask)}
          dataSource={taskList || []}
          onPageChange={handlePageChange}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isOpenConfirm && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
  );
};

export default TaskToday;
